import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { AppContext } from "../../../../appContext";
import { getNotes, postNote, putNote, deleteNote } from "../../../../api/notesApi";
import { ConfirmationPopup } from "../../../../components/confirmationPopup/ConfirmationPopup";
import { addNoteReloadListener, removeNoteReloadListener, triggerNoteReload } from "../../../../utils/dataSyncTriggers";
import './MeetingPageNotesWidget.css';

interface INote {
    id?: string;
    title: string;
    text: string;
    date: Date;
    createdAt?: string;
    updatedAt?: string;
    client?: string;
}

interface IMeetingPageNotesWidgetProps {
    selectedClientId?: string;
    selectedClientName?: string;
}

export const MeetingPageNotesWidget = ({ selectedClientId, selectedClientName }: IMeetingPageNotesWidgetProps) => {
    const { translate } = useContext(AppContext);
    const [notes, setNotes] = useState<INote[]>([]);
    const [loading, setLoading] = useState(true);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [filterByClient, setFilterByClient] = useState(false);
    const [expandedNoteId, setExpandedNoteId] = useState<string | null>(null);
    const [editingNote, setEditingNote] = useState<INote | null>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [boundToClient, setBoundToClient] = useState(false);
    const editorRef = useRef<HTMLDivElement>(null);
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isEditingRef = useRef(false);
    const skipNextReloadRef = useRef(false);

    // Refs that always hold the latest state values — used in flush callbacks to avoid stale closures
    const editingNoteRef = useRef<INote | null>(null);
    const boundToClientRef = useRef(false);
    const isDirtyRef = useRef(false);
    const flushPendingSaveRef = useRef<(() => void) | null>(null);

    useEffect(() => { editingNoteRef.current = editingNote; }, [editingNote]);
    useEffect(() => { boundToClientRef.current = boundToClient; }, [boundToClient]);

    const loadNotes = useCallback(async (force: boolean = false) => {
        if (skipNextReloadRef.current) {
            skipNextReloadRef.current = false;
            return;
        }
        if (isEditingRef.current && !force) {
            return;
        }
        
        try {
            setLoading(true);
            const response = await getNotes();
            if (response && response.ok) {
                const data = await response.json();
                const parsedNotes = data.map((item: any) => {
                    const noteId = Object.keys(item)[0];
                    return {
                        id: noteId,
                        title: item[noteId].title || 'Untitled',
                        text: item[noteId].text || '',
                        date: item[noteId].date ? new Date(item[noteId].date) : new Date(),
                        createdAt: item[noteId].createdAt,
                        updatedAt: item[noteId].updatedAt,
                        client: item[noteId].client
                    };
                });
                
                const sortedNotes = parsedNotes.sort((a: INote, b: INote) => {
                    const dateA = new Date(a.updatedAt || a.createdAt || a.date || 0).getTime();
                    const dateB = new Date(b.updatedAt || b.createdAt || b.date || 0).getTime();
                    return dateB - dateA;
                });
                
                setNotes(sortedNotes);
            }
        } catch (err) {
            console.error('Failed to load notes:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    useEffect(() => {
        if (selectedClientId) {
            loadNotes();
        }
    }, [selectedClientId, loadNotes]);

    useEffect(() => {
        addNoteReloadListener(loadNotes);
        return () => {
            removeNoteReloadListener(loadNotes);
        };
    }, [loadNotes]);

    const handleCreateNote = async () => {
        const shouldBindToClient = (filterByClient || boundToClient) && selectedClientId;
        const newNote: INote = {
            title: translate('notes_new_title') || 'New Note',
            text: '',
            date: new Date(),
            client: shouldBindToClient ? selectedClientId : undefined
        };

        try {
            const postResponse = await postNote({
                title: newNote.title,
                text: newNote.text,
                date: newNote.date,
                tags: [],
                client: newNote.client
            });
            
            if (!postResponse || !postResponse.ok) return;

            // Server doesn't return the ID in POST response — fetch all notes and find the new one
            const listResponse = await getNotes();
            if (!listResponse || !listResponse.ok) return;
            const listData = await listResponse.json();

            const parsedAll: INote[] = listData.map((item: any) => {
                const id = Object.keys(item)[0];
                return {
                    id,
                    title: item[id].title || '',
                    text: item[id].text || '',
                    date: item[id].date ? new Date(item[id].date) : new Date(),
                    createdAt: item[id].createdAt,
                    updatedAt: item[id].updatedAt,
                    client: item[id].client
                };
            });

            // Find the note we just created: matching title, client, and most recent date
            const matchingNotes = parsedAll.filter(n => n.title === newNote.title && n.client === newNote.client);
            const savedNote = matchingNotes.sort((a, b) =>
                new Date(b.createdAt || b.date).getTime() - new Date(a.createdAt || a.date).getTime()
            )[0];

            if (!savedNote || !savedNote.id) {
                console.error('[MeetingNotes] Could not find created note in list');
                return;
            }

            console.log('[MeetingNotes] resolved noteId:', savedNote.id);
            setNotes(parsedAll.sort((a, b) =>
                new Date(b.updatedAt || b.createdAt || b.date).getTime() - new Date(a.updatedAt || a.createdAt || a.date).getTime()
            ));
            setExpandedNoteId(savedNote.id);
            setEditingNote(savedNote);
            isEditingRef.current = true;
            isDirtyRef.current = false;
            skipNextReloadRef.current = true;
            triggerNoteReload();
        } catch (err) {
            console.error('Failed to create note:', err);
        }
    };

    const autoSaveNote = async (noteToSave: INote) => {
        if (!noteToSave || !noteToSave.id) {
            console.warn('[MeetingNotes] autoSaveNote: no note or no id', noteToSave);
            return;
        }
        
        const clientId = boundToClientRef.current && selectedClientId ? selectedClientId : noteToSave.client;
        console.log('[MeetingNotes] autoSaveNote: saving id=', noteToSave.id, 'title=', noteToSave.title, 'text length=', noteToSave.text?.length);
        
        setIsSaving(true);
        try {
            const res = await putNote({
                title: noteToSave.title,
                text: noteToSave.text,
                date: noteToSave.date,
                tags: [],
                client: clientId
            }, noteToSave.id);
            console.log('[MeetingNotes] putNote response status:', res?.status, 'ok:', res?.ok);
            if (!res || !res.ok) {
                const body = res ? await res.text() : 'null response';
                console.error('[MeetingNotes] putNote FAILED, server response:', body);
                return;
            }
            setNotes(prev => prev.map(n => n.id === noteToSave.id ? noteToSave : n));
            isDirtyRef.current = false;
        } catch (err) {
            console.error('[MeetingNotes] Failed to save note:', err);
        } finally {
            setIsSaving(false);
        }
    };

    // Always keep a reference to the latest flush function (avoids stale closures in unmount)
    const flushPendingSave = () => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = null;
        }
        if (editingNoteRef.current && editingNoteRef.current.id && isDirtyRef.current) {
            autoSaveNote(editingNoteRef.current);
        }
    };

    useEffect(() => {
        flushPendingSaveRef.current = flushPendingSave;
    });

    // Flush pending save on unmount
    useEffect(() => {
        return () => {
            flushPendingSaveRef.current?.();
        };
    }, []);

    // Flush when tab is hidden or page is closed
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                flushPendingSaveRef.current?.();
            }
        };
        const handleBeforeUnload = () => {
            flushPendingSaveRef.current?.();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    // Debounced auto-save: fires 1500ms after last change
    useEffect(() => {
        if (!editingNote || !editingNote.id) return;
        
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        
        saveTimeoutRef.current = setTimeout(() => {
            autoSaveNote(editingNote);
        }, 1500);
        
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [editingNote?.title, editingNote?.text]);

    const handleDeleteNote = async (noteId: string) => {
        try {
            await deleteNote(noteId);
            setNotes(prev => prev.filter(n => n.id !== noteId));
            setExpandedNoteId(null);
            setDeleteConfirmId(null);
            skipNextReloadRef.current = true;
            triggerNoteReload();
        } catch (err) {
            console.error('Failed to delete note:', err);
        }
    };

    const toggleNoteExpanded = (noteId: string) => {
        if (expandedNoteId === noteId) {
            flushPendingSave();
            setExpandedNoteId(null);
            setEditingNote(null);
            isEditingRef.current = false;
            isDirtyRef.current = false;
        } else {
            if (editingNote) {
                flushPendingSave();
            }
            setExpandedNoteId(noteId);
            const note = notes.find(n => n.id === noteId);
            if (note) {
                setBoundToClient(!!note.client);
            }
        }
    };

    useEffect(() => {
        if (editingNote && editorRef.current) {
            editorRef.current.innerHTML = editingNote.text || '';
        }
    }, [editingNote?.id]);

    const handleEditorInput = () => {
        if (!editorRef.current || !editingNote) return;
        const currentHTML = editorRef.current.innerHTML;
        isEditingRef.current = true;
        isDirtyRef.current = true;
        setEditingNote(prev => prev ? { ...prev, text: currentHTML } : null);
    };

    const formatDate = (dateString: string | Date | undefined) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, { 
            month: 'short', 
            day: 'numeric'
        });
    };

    const formatText = (command: string, value?: string) => {
        document.execCommand(command, false, value);
        editorRef.current?.focus();
    };

    const filteredNotes = filterByClient && selectedClientId
        ? notes.filter(note => note.client === selectedClientId)
        : notes;

    return (
        <div className={`meetingPageNotes ${isCollapsed ? 'meetingPageNotes--collapsed' : ''}`}>
            <div className="meetingPageNotes_header" onClick={() => setIsCollapsed(!isCollapsed)}>
                <div className="meetingPageNotes_title_wrapper">
                    <div className="meetingPageNotes_title">
                        {translate('notes_widget_title') || 'Notes'}
                    </div>
                    {selectedClientId && (
                        <div 
                            className={`meetingPageNotes_filter_icon ${!filterByClient ? 'meetingPageNotes_filter_icon--active' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setFilterByClient(!filterByClient);
                            }}
                            title={filterByClient 
                                ? translate('filter_show_client_notes')?.replace('{name}', selectedClientName || '')
                                : translate('filter_show_all_notes')
                            }
                        />
                    )}
                </div>
                <div className="meetingPageNotes_toggle">
                    <div className="meetingPageNotes_toggle_icon"></div>
                </div>
            </div>
            
            <div className="meetingPageNotes_drop">
                <div className="meetingPageNotes_drop_content">
                    {loading ? (
                        <div className="meetingPageNotes_loading">{translate('common_loading') || 'Loading...'}</div>
                    ) : (
                        <div className="meetingPageNotes_content">
                            <div className="meetingPageNotes_list">
                                {filteredNotes.slice(0, 5).map((note) => {
                                    const isExpanded = expandedNoteId === note.id;
                                    
                                    return (
                                        <div key={note.id} className={`meetingPageNotes_item ${isExpanded ? 'meetingPageNotes_item--expanded' : ''}`}>
                                            <div className="meetingPageNotes_item_head" onClick={() => !isExpanded && toggleNoteExpanded(note.id!)}>
                                                <div className="meetingPageNotes_item_left">
                                                    <div className="meetingPageNotes_item_title">
                                                        {note.title}
                                                    </div>
                                                    <div className="meetingPageNotes_item_date">
                                                        {formatDate(note.updatedAt || note.createdAt || note.date)}
                                                    </div>
                                                </div>
                                                <div className="meetingPageNotes_item_actions">
                                                    {isExpanded && editingNote?.id !== note.id && (
                                                        <div 
                                                            className="meetingPageNotes_edit_button" 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                flushPendingSave();
                                                                setBoundToClient(!!note.client);
                                                                setEditingNote(note);
                                                                isDirtyRef.current = false;
                                                            }}
                                                        />
                                                    )}
                                                    <div className="meetingPageNotes_open_button" onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleNoteExpanded(note.id!);
                                                    }} />
                                                </div>
                                            </div>
                                            
                                            {isExpanded && editingNote?.id !== note.id && (
                                                <div className="meetingPageNotes_item_preview">
                                                    <div className="meetingPageNotes_preview_text" dangerouslySetInnerHTML={{ __html: note.text || '' }} />
                                                </div>
                                            )}
                                            
                                            {isExpanded && editingNote && (
                                                <div className="meetingPageNotes_item_content">
                                                    <div className="meetingPageNotes_edit_section">
                                                        <input
                                                            type="text"
                                                            className="meetingPageNotes_title_input"
                                                            value={editingNote.title}
                                                            onChange={(e) => {
                                                                isEditingRef.current = true;
                                                                isDirtyRef.current = true;
                                                                setEditingNote({ ...editingNote, title: e.target.value });
                                                            }}
                                                            placeholder={translate('notes_title_placeholder') || 'Note title'}
                                                        />
                                                        
                                                        {selectedClientId && (
                                                            <label className="meetingPageNotes_bound_toggle">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={boundToClient}
                                                                    onChange={(e) => {
                                                                        const newBound = e.target.checked;
                                                                        setBoundToClient(newBound);
                                                                        isDirtyRef.current = true;
                                                                        isEditingRef.current = true;
                                                                        setEditingNote(prev => prev ? { 
                                                                            ...prev, 
                                                                            client: newBound && selectedClientId ? selectedClientId : undefined 
                                                                        } : null);
                                                                    }}
                                                                />
                                                                <span>{translate('notes_bound_to_client')}</span>
                                                            </label>
                                                        )}
                                                        
                                                        <div className="meetingPageNotes_toolbar">
                                                            <button onClick={() => formatText('bold')} title="Bold" className="meetingPageNotes_toolbar_btn">
                                                                <strong>B</strong>
                                                            </button>
                                                            <button onClick={() => formatText('italic')} title="Italic" className="meetingPageNotes_toolbar_btn">
                                                                <em>I</em>
                                                            </button>
                                                            <button onClick={() => formatText('underline')} title="Underline" className="meetingPageNotes_toolbar_btn">
                                                                <u>U</u>
                                                            </button>
                                                        </div>
                                                        
                                                        <div
                                                            ref={editorRef}
                                                            className="meetingPageNotes_text_editor"
                                                            contentEditable
                                                            onInput={handleEditorInput}
                                                            suppressContentEditableWarning
                                                        />
                                                        
                                                        {isSaving && (
                                                            <div className="meetingPageNotes_saving_indicator">
                                                                {translate('common_saving') || 'Saving...'}
                                                            </div>
                                                        )}
                                                    </div>
                                                    
                                                    <div className="meetingPageNotes_footer">
                                                        <div 
                                                            className="meetingPageNotes_delete_icon"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDeleteConfirmId(note.id!);
                                                            }}
                                                            title="Delete"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="meetingPageNotes_actions">
                                <div className="meetingPageNotes_add_wrapper" onClick={handleCreateNote}>
                                    <div className="meetingPageNotes_add_icon"></div>
                                    <span className="meetingPageNotes_add_text">{translate('notes_add_new') || 'Add a new note'}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {deleteConfirmId && (
                <ConfirmationPopup
                    message={translate('notes_confirm_delete') || 'Are you sure you want to delete this note?'}
                    confirmText={translate('common_delete') || 'Delete'}
                    cancelText={translate('common_cancel') || 'Cancel'}
                    onConfirm={() => handleDeleteNote(deleteConfirmId)}
                    onCancel={() => setDeleteConfirmId(null)}
                    confirmButtonStyle="danger"
                />
            )}
        </div>
    );
};
